import React, { useCallback, useState } from "react";
import { Col, Row, Stack } from "react-bootstrap";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";

import "./InsuranceBonusPage.scss";
import PrimaryActionButton from "../../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import FixedFooter from "../../../components/FixedFooter/FixedFooter";
import useTopupFlow from "../../../hooks/useTopupFlow";
import TopupFlow from "../../../components/topup/TopupFlow/TopupFlow";
import useAnalytics from "../../../hooks/useAnalytics";
import { SignupBonusOptionProperty } from "../../../utils/analytics";
import {
  initiateTopupTriggerAtom,
  InitiateTopupTriggers,
} from "../../../jotai/atoms/analytics";
import OnboardingContainer from "../../../components/onboarding/OnboardingContainer/OnboardingContainer";
import SecondaryActionButton from "../../../components/Buttons/SecondaryActionButton/SecondaryActionButton";
import CurrencySelectorDropdown from "../../../components/onboarding/CurrencySelectorDropdown/CurrencySelectorDropdown";
import {
  FiatCurrency,
  PurchasePlan,
} from "@nestcoinco/hyper-api-gateway-api-client";
import usePurchasePlans from "../../../hooks/usePurchasePlans";
import TopupBundle from "../../../components/topup/TopupBundle/TopupBundle";
import useAuth from "../../../hooks/useAuth";
import useDisclosure from "../../../hooks/useDisclosure";
import { ThemeVariant } from "../../../utils/constants/generic-types";
import WelcomeBonusFaqsDrawer from "../../../components/onboarding/WelcomeBonusFaqsDrawer/WelcomeBonusFaqsDrawer";
import useInitiateTopup, {
  InitiatingPaymentLoader,
} from "../../../hooks/useInitiateTopup";
import CompleteUsdTopup from "../../../components/topup/CompleteUsdTopup/CompleteUsdTopup";

function InsuranceBonusPage() {
  const { userProfile } = useAuth();
  const userCurrency = userProfile?.currency;
  const { t } = useTranslation("common", { keyPrefix: "onboarding" });
  const { sendSignupBonusOptionsEvent } = useAnalytics();
  const setInitiateTopupTrigger = useSetAtom(initiateTopupTriggerAtom);
  const [selectedCurrency, setSelectedCurrency] = useState<FiatCurrency>();
  const { welcomeInsurancePlans, bestDealId, plansMetadataObj } =
    usePurchasePlans(selectedCurrency);
  const {
    loading,
    initiatingPaymentState,
    initPayment,
    selectedPurchasePlanId,
    setSelectedPurchasePlanId,
    selectedPurchasePlan,
    selectedPurchasePlanIsSpecial,
    setSelectedPurchasePlanIsSpecial,
  } = useInitiateTopup({
    purchasePlans: welcomeInsurancePlans,
    plansMetadataObj,
  });
  const {
    isFirstTopup,
    modalsProps,
    ipLocationCountry,
    handleTopup,
    handleGetDeviceLocationInfo,
  } = useTopupFlow({ isCustomFlow: true });
  const {
    isOpen: isCompleteUsdPaymentOpen,
    onOpen: openCompleteUsdPayment,
    onClose: closeCompleteUsdPayment,
  } = useDisclosure();
  const {
    isOpen: isWelcomeBonusFaqsOpen,
    onOpen: openWelcomeBonusFaqs,
    onClose: closeWelcomeBonusFaqs,
  } = useDisclosure();

  const handleSuccessfulPrelimTopupChecks = useCallback(() => {
    if (userCurrency === FiatCurrency.USD) {
      openCompleteUsdPayment();
    } else {
      initPayment();
    }
  }, [initPayment, openCompleteUsdPayment, userCurrency]);

  const handleClaimBonus = useCallback(() => {
    sendSignupBonusOptionsEvent(SignupBonusOptionProperty.CLAIM_BONUS);
    setInitiateTopupTrigger(
      InitiateTopupTriggers.ONBOARDING_CLAIM_INSURANCE_BONUS
    );
    handleTopup({ onSuccess: handleSuccessfulPrelimTopupChecks });
  }, [
    handleTopup,
    sendSignupBonusOptionsEvent,
    setInitiateTopupTrigger,
    handleSuccessfulPrelimTopupChecks,
  ]);

  const updateSelectedPurchasePlan = (
    purchasePlanId: PurchasePlan["id"],
    isSpecialOffer = false
  ) => {
    setSelectedPurchasePlanId(purchasePlanId);
    setSelectedPurchasePlanIsSpecial(isSpecialOffer);
  };

  return (
    <>
      <OnboardingContainer className="InsuranceBonusPage">
        <Stack gap={4} className="main-content">
          <Stack gap={2} className="header">
            <h1 className="main-copy">
              {t("hundred_percent_bonus_unlocked_claim_now")}!
            </h1>
            <p className="sub-copy">
              {t("top_up_to_play_in_real_money_contests")}
            </p>
          </Stack>
          <Stack className="bundle-selection-section">
            {isFirstTopup && (
              <CurrencySelectorDropdown
                showConfirmationModal
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
              />
            )}
            <p className="players-claimed-stats">
              🔥 {t("over_2000_players_claimed_this_in_the_last_48_hours")}!
            </p>
            <Row className="welcome-bonus-bundles">
              {welcomeInsurancePlans?.map((plan) => {
                const isBestDeal = plan.id === bestDealId;
                return (
                  <Col
                    key={plan.id}
                    className={`p-0 ${isBestDeal ? "best-deal-bundle" : ""}`}
                  >
                    <TopupBundle
                      theme={ThemeVariant.DARK}
                      plan={plan}
                      isBestDeal={isBestDeal}
                      metadataObj={plansMetadataObj[plan.id]}
                      onClick={() => updateSelectedPurchasePlan(plan.id, true)}
                      isSelected={
                        selectedPurchasePlanId === plan.id &&
                        selectedPurchasePlanIsSpecial
                      }
                    />
                  </Col>
                );
              })}
            </Row>
          </Stack>
        </Stack>
        <FixedFooter className="d-flex flex-column gap-3 cta-container">
          <PrimaryActionButton
            size="lg"
            text={t("dashboard:claim_bonus")}
            onClick={handleClaimBonus}
          />
          <SecondaryActionButton
            text={`${t("not_sure_yet")}?`}
            buttonClassName="not-sure-btn"
            onClick={openWelcomeBonusFaqs}
          />
        </FixedFooter>
      </OnboardingContainer>

      <WelcomeBonusFaqsDrawer
        isOpen={isWelcomeBonusFaqsOpen}
        onClose={closeWelcomeBonusFaqs}
      />
      <TopupFlow
        {...modalsProps}
        handleTopup={handleTopup}
        ipLocationCountry={ipLocationCountry}
        handleGetDeviceLocationInfo={handleGetDeviceLocationInfo}
      />
      {selectedPurchasePlan && (
        <CompleteUsdTopup
          isOpen={isCompleteUsdPaymentOpen}
          onClose={closeCompleteUsdPayment}
          handleInitPayment={initPayment}
          metadataObj={plansMetadataObj[selectedPurchasePlan.id]}
          isBestDeal={selectedPurchasePlanId === bestDealId}
          selectedPurchasePlan={selectedPurchasePlan as PurchasePlan}
          selectedPurchasePlanIsSpecial={selectedPurchasePlanIsSpecial}
        />
      )}

      {loading && (
        <InitiatingPaymentLoader
          state={initiatingPaymentState}
          theme={ThemeVariant.DARK}
        />
      )}
    </>
  );
}

export default InsuranceBonusPage;
